import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Badge, Spinner, RequireTenant } from "../ui";
import { useAuth } from "../AuthContext";
import { Check, Wallet, Zap } from "lucide-react";
import { toast } from "sonner";

const TXN_COLOR = { pending: "amber", paid: "green", failed: "red" };

function BillingInner() {
  const { activeTenant } = useAuth();
  const [plans, setPlans] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: w }, { data: t }] = await Promise.all([
        api.get("/plans"), api.get("/billing/wallet"), api.get("/billing/transactions"),
      ]);
      setPlans(p.items); setWallet(w); setTxns(t.items);
    } finally { setLoading(false); }
  }, [activeTenant]);
  useEffect(() => { load(); }, [load]);

  const buy = async (code) => {
    setBusy(code);
    try {
      const { data } = await api.post("/billing/checkout", { plan_code: code });
      // MOCK payment: immediately confirm the checkout session (stands in for Stripe webhook).
      await api.post(`/billing/checkout/${data.session_id}/confirm`);
      toast.success(`Plan ${code} activat, ${data.lead_quota} lead-uri adaugate`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      {wallet && (
        <Card className="p-6 mb-6 bg-slate-900 text-white border-slate-900">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
              <div>
                <div className="text-sm text-slate-300">Lead-uri disponibile</div>
                <div className="text-3xl font-semibold">{wallet.lead_quota_remaining} <span className="text-base font-normal text-slate-400">/ {wallet.lead_quota_total}</span></div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-300">Total platit</div>
              <div className="text-2xl font-semibold">{wallet.amount_paid_eur.toFixed(2)} EUR</div>
            </div>
          </div>
        </Card>
      )}

      <h3 className="font-medium text-slate-900 mb-3">Alege un pachet</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="plans-grid">
        {plans.map((p) => (
          <Card key={p.code} className="p-5 flex flex-col" data-testid={`plan-${p.code}`}>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-600" />
              <h4 className="font-semibold text-slate-900">{p.name}</h4>
            </div>
            <div className="mt-4 text-3xl font-semibold text-slate-900">{p.price_eur.toFixed(0)}<span className="text-base font-normal text-slate-400"> EUR</span></div>
            <div className="text-sm text-slate-500 mt-1">{p.lead_quota.toLocaleString()} lead-uri</div>
            <div className="text-xs text-slate-400 mt-0.5">{p.per_lead_price} EUR / lead</div>
            <Btn className="mt-5 w-full" onClick={() => buy(p.code)} disabled={!!busy} data-testid={`buy-${p.code}`}>
              {busy === p.code ? "Se proceseaza…" : "Cumpara"}
            </Btn>
          </Card>
        ))}
      </div>

      <h3 className="font-medium text-slate-900 mb-3">Istoric tranzactii</h3>
      <Card>
        {txns.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">Nicio tranzactie inca.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-3 font-medium">Sesiune</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium">Lead-uri</th>
                  <th className="px-5 py-3 font-medium">Suma</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.session_id} className="border-b border-slate-50">
                    <td className="px-5 py-3 font-mono text-xs text-slate-400">{t.session_id}</td>
                    <td className="px-5 py-3 text-slate-700">{t.plan_code}</td>
                    <td className="px-5 py-3 text-slate-700">{t.lead_quota}</td>
                    <td className="px-5 py-3 text-slate-700">{t.amount_eur} EUR</td>
                    <td className="px-5 py-3"><Badge color={TXN_COLOR[t.status]}>{t.status === "paid" && <Check className="w-3 h-3 mr-1" />}{t.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <p className="text-xs text-slate-400 mt-3">Platile sunt simulate (MOCK). Integrarea reala Stripe se va conecta pe aceeasi interfata.</p>
    </div>
  );
}

export default function Billing() {
  return (
    <div>
      <PageHeader title="Facturare" subtitle="Pachete de lead-uri preplatite si istoricul tranzactiilor." />
      <RequireTenant><BillingInner /></RequireTenant>
    </div>
  );
}
